import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Check, X } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

interface PasswordCriteria {
  label: string;
  test: (password: string) => boolean;
}

interface PasswordStrengthIndicatorProps {
  password: string;
}

const passwordCriteria: PasswordCriteria[] = [
  {
    label: 'Pelo menos 8 caracteres',
    test: (password: string) => password.length >= 8,
  },
  {
    label: 'Pelo menos uma letra maiúscula (A-Z)',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    label: 'Pelo menos uma letra minúscula (a-z)',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    label: 'Pelo menos um número (0-9)',
    test: (password: string) => /[0-9]/.test(password),
  },
  {
    label: 'Pelo menos um caractere especial (!@#$%^&*)',
    test: (password: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  },
];

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const { colors } = useTheme();

  const getMetCriteria = () => {
    return passwordCriteria.filter(criteria => criteria.test(password));
  };

  const isAllCriteriaMet = () => {
    return passwordCriteria.every(criteria => criteria.test(password));
  };

  const styles = StyleSheet.create({
    container: {
      marginTop: 12,
      padding: 16,
      backgroundColor: colors.surface,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    title: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 12,
    },
    criteriaList: {
      gap: 8,
    },
    criteriaItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    criteriaText: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      flex: 1,
    },
    criteriaTextMet: {
      color: '#10B981', // Verde
    },
    criteriaTextNotMet: {
      color: colors.textSecondary,
    },
    strengthBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      marginTop: 12,
      overflow: 'hidden',
    },
    strengthProgress: {
      height: '100%',
      borderRadius: 2,
    },
    strengthText: {
      fontSize: 12,
      fontFamily: 'Inter-Medium',
      marginTop: 4,
      textAlign: 'center',
    },
  });

  const metCriteria = getMetCriteria();
  const strengthPercentage = (metCriteria.length / passwordCriteria.length) * 100;
  
  const getStrengthColor = () => {
    if (strengthPercentage === 0) return colors.textSecondary;
    if (strengthPercentage <= 40) return '#EF4444'; // Vermelho
    if (strengthPercentage <= 80) return '#F59E0B'; // Amarelo
    return '#10B981'; // Verde
  };

  const getStrengthText = () => {
    if (strengthPercentage === 0) return 'Digite uma senha';
    if (strengthPercentage <= 40) return 'Senha fraca';
    if (strengthPercentage <= 80) return 'Senha média';
    return 'Senha forte';
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Requisitos da senha:</Text>
      
      <View style={styles.criteriaList}>
        {passwordCriteria.map((criteria, index) => {
          const isMet = criteria.test(password);
          return (
            <View key={index} style={styles.criteriaItem}>
              {isMet ? (
                <Check size={16} color="#10B981" />
              ) : (
                <X size={16} color={colors.textSecondary} />
              )}
              <Text 
                style={[
                  styles.criteriaText,
                  isMet ? styles.criteriaTextMet : styles.criteriaTextNotMet
                ]}
              >
                {criteria.label}
              </Text>
            </View>
          );
        })}
      </View>

      <View style={styles.strengthBar}>
        <View 
          style={[
            styles.strengthProgress,
            {
              width: `${strengthPercentage}%`,
              backgroundColor: getStrengthColor(),
            }
          ]} 
        />
      </View>
      
      <Text style={[styles.strengthText, { color: getStrengthColor() }]}>
        {getStrengthText()}
      </Text>
    </View>
  );
}

export { passwordCriteria };
export type { PasswordCriteria };